import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx';
import { Box, CircularProgress, ClickAwayListener, Grow, List, ListItem, ListItemText, Paper, Popper, TablePagination, makeStyles, IconButton } from '@material-ui/core';
import useAbortableFetch from '../../../hooks/useAbortableFetch';
import BrandTable from '../../CoreComponents/BrandTable';
import TablePaginationActions from '../../CoreComponents/TablePaginationActions';
import { SONG_PORTFOLIO_DEFAULT_ROWS_PER_PAGE, SONG_PORTFOLIO_ROWS_PER_PAGE_OPTIONS, handleRowsPerPageChange } from '../../utils/PaginationUtils';
import { formatNumber } from '../../utils/NumberUtils';
import { LOCALE_EN_CA } from '../../utils/DateUtils';
import { reportTypeIds } from '../../../constants/reportConstants';
import { toNumber } from 'lodash';
import { deleteData, download } from '../../utils/FetchUtils';
import { useStoreContext } from '../../../store/Store';
import { userAccessByType } from '../../utils/AccessUtils';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DeleteReportModal from './DeleteReportModal';
import { useSnackbarAlert } from '../../../hooks/useSnackbarAlert';

const useStyles = makeStyles((theme) => ({
    container: {
        '& .MuiTableRow-root': {
            font: 'normal normal medium 14px/17px Roboto',
        },
        "& .MuiTablePagination-root": {
            '& .MuiTablePagination-selectRoot': {
                "& .MuiSvgIcon-root": {
                    color: 'white'
                }
            }
        },
    },
    status: {
        font: 'normal normal medium 17px/28px Roboto',
        letterSpacing: '0px',
        color: theme.palette.text.greenNuanceVersion3,
    },
    reportingPeriod: {
        display: 'flex',
        alignItems: 'center',
    },
    icon: {
        fontSize: '18px',
        marginLeft: theme.spacing(1),
    },
    reportingPopper: {
        zIndex: 1,
        border: `1px solid ${theme.palette.primary.border}`,
        borderRadius: '10px',
    },
    menuItem: {
        font: 'normal normal medium 14px/21px Roboto',
        letterSpacing: '0px',
        textAlign: 'left',
        width: '150px',
        '& > :first-child': {
            marginRight: theme.spacing(1),
        },
    },
    deleteText: {
        color: theme.palette.primary.delete,
    },
    redIcon: {
        color: theme.palette.primary.delete,
    },
    actionContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        paddingBottom: theme.spacing(2)
    },
    feeCell: {
        color: theme.palette.primary.delete,
    },
    royaltyPayableCell: {
        color: theme.palette.primary.main,
    },
    naContainer: {
        color: theme.palette.primary.alert,
    },
    iconButtonGroup: {
        display: 'flex',
        justifyContent: 'flex-end'
    },
}));

const CsvDataTable = (
    {
        accountHolder,
        startDate,
        endDate,
        selectedFilter,
        reportNameSearch,
        selectedReportingCompanies
    }
) => {
    const abortableFetch = useAbortableFetch();
    const [state] = useStoreContext();
    const styles = useStyles();
    const { showSuccessAlert, showErrorAlert } = useSnackbarAlert();

    const [items, setItems] = useState([]);
    const [itemsCount, setItemsCount] = useState(0);

    const [isDownloading, setIsDownloading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [reportData, setReportData] = useState({
        id: null,
        reportName: null,
    })

    const handleDownload = async (reportFileId, reportName) => {
        if (isDownloading)
            return;
        setIsDownloading(true);
        try {
            await download(process.env.REACT_APP_SERVER_HOST + '/api/report/report-files-stream', { reportIds: [reportFileId] }, 'csv', reportName);
            showSuccessAlert('Download finished.');
        }
        catch (error) {
            console.error(error);
            showErrorAlert(error.message);
        } finally {
            setIsDownloading(false);
        }
    }

    const handleDelete = async () => {
        if (!reportData.id)
            throw new Error("Invalid report ID.");

        await deleteData(process.env.REACT_APP_SERVER_HOST + '/api/report/',
            {
                id: reportData.id,
            }
        );
        setItems(prevItems => prevItems.filter(item => item.reportFileId !== reportData.id));
    }

    const isAdmin = state.user && state.user.userType === userAccessByType.USER_TYPE_ADMIN_ACCESS;
    const adminOnlyRows = ['actions', 'reportingCompany', 'status'];

    const [headCells] = useState([
        {
            id: 'accountHolderName',
            label: 'Account Holder',
            minWidth: '13%',
            maxWidth: '13%',
            popover: true,
            CellRender: ({ rowData }) => {
                return (
                    <div className={styles.rowItemFirst}>
                        {rowData.accountHolderName}
                    </div>
                )
            }
        },
        {
            id: 'fileName',
            label: 'Report Name',
            minWidth: '23%',
            maxWidth: '23%',
            popover: true,
            CellRender: ({ rowData, className }) => {
                return (
                    <div className={className}>
                        {rowData.fileName}
                    </div>
                );
            }
        },
        {
            id: 'reportType',
            label: 'Report Type',
            minWidth: '12%',
            maxWidth: '12%',
            CellRender: ({ rowData }) => {
                const getReportingType = (value) => {
                    const type = toNumber(value);
                    switch (type) {
                        case reportTypeIds.PRO: return "PRO";
                        case reportTypeIds.MASTER_PERFORMANCE: return "Master Performance";
                        case reportTypeIds.PUBLISHER_ADMIN: return "Publisher / Admin";
                        case reportTypeIds.LABEL_DISTRIBUTOR: return "Label / Distributor";
                    }
                }

                return (
                    <div>
                        {getReportingType(rowData.reportType)}
                    </div>
                );
            },
        },
        {
            id: 'reportingCompany',
            label: 'Reporting Company',
            minWidth: '12%',
            maxWidth: '12%',
            CellRender: ({ rowData }) => {
                return (
                    <div>
                        {rowData.reportingCompanyName}
                    </div>
                );
            },
        },
        {
            id: 'value',
            label: 'Total Revenue',
            minWidth: '10%',
            maxWidth: '10%',
            popover: true,
            CellRender: ({ rowData }) => {
                return (
                    <div>
                        {`$${formatNumber(rowData.value)}`}
                    </div>

                );
            }
        },
        {
            id: 'fee',
            label: 'Fee',
            minWidth: '10%',
            maxWidth: '10%',
            popover: true,
            CellRender: ({ rowData }) => {
                return (
                    <div className={clsx(styles.rowItemMiddle, styles.feeCell)}>
                        {rowData === null ?
                            <div className={styles.naContainer}>N/A</div>
                            :
                            `$${formatNumber(rowData.fee)}`
                        }
                    </div>

                );
            }
        },
        {
            id: 'valueAfterFees',
            label: 'Royalty Payable',
            minWidth: '10%',
            maxWidth: '10%',
            popover: true,
            CellRender: ({ rowData }) => {
                return (
                    <div className={clsx(styles.rowItemMiddle, styles.royaltyPayableCell)}>
                        {rowData === null ?
                            <div className={styles.naContainer}>N/A</div>
                            :
                            `$${formatNumber(rowData.valueAfterFees)}`
                        }
                    </div>

                );
            }
        },
        {
            id: 'actions',
            label: '',
            notSortable: true,
            minWidth: '7%',
            maxWidth: '7%',
            CellRender: ({ rowData }) => {
                const [open, setOpen] = useState(false);
                const anchorRef = useRef(null);

                const handleMenuToggle = () => {
                    setOpen((prevOpen) => !prevOpen);
                };

                const handleMenuClose = (event) => {
                    if (anchorRef.current && anchorRef.current.contains(event.target))
                        return;
                    setOpen(false);
                };

                const handleListKeyDown = (event) => {
                    if (event.key === 'Tab') {
                        event.preventDefault();
                        setOpen(false);
                    } else if (event.key === 'Escape') {
                        setOpen(false);
                    }
                }

                // TODO: Currently hide the menu options and list the icon actions only to save space

                return (
                    <Box className={styles.iconButtonGroup}>
                        <IconButton
                            onClick={() => {
                                setReportData({ id: rowData.reportFileId, reportName: rowData.fileName })
                                setShowDeleteModal(true);
                            }}>
                            <DeleteIcon className={styles.redIcon} />
                        </IconButton>
                        <IconButton
                            onClick={() => { handleDownload(rowData.reportFileId, rowData.fileName);}}>
                            <FileDownloadIcon />
                        </IconButton>
                    </Box>
                );
            }
        }
    ].filter((headCell) => isAdmin || adminOnlyRows.indexOf(headCell.id) === -1));

    const [rowsPerPage, setRowsPerPage] = useState(SONG_PORTFOLIO_DEFAULT_ROWS_PER_PAGE); // SONG_PORTFOLIO_DEFAULT_ROWS_PER_PAGE
    const [rowsPerPageOptions] = useState(SONG_PORTFOLIO_ROWS_PER_PAGE_OPTIONS); // SONG_PORTFOLIO_ROWS_PER_PAGE_OPTIONS
    const [sortBy, setSortBy] = useState('id');
    const [sortType, setSortType] = useState('ASC');
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            let startDateString = undefined, endDateString = undefined;
            if (startDate && endDate) {
                startDateString = startDate.toLocaleDateString(LOCALE_EN_CA);
                endDateString = endDate.toLocaleDateString(LOCALE_EN_CA);
            }

            const body = {
                filter: reportNameSearch,
                startDate: startDateString,
                endDate: endDateString,
                sort: sortBy,
                type: sortType,
                reportTypeFilter: selectedFilter,
                accountHolder: accountHolder,
                selectedReportingCompanies,
            };
            try {
                const fetchedData = await abortableFetch('POST',
                    '/api/elastic/csv-statements', {
                    body,
                    query: {
                        offset,
                        limit: rowsPerPage
                    }
                });

                setItems(fetchedData ? fetchedData.statementsData : []);
                setItemsCount(fetchedData ? fetchedData.statementsDataEntries : 0);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [startDate, endDate, offset, rowsPerPage, sortType, sortBy, selectedFilter, accountHolder, reportNameSearch, selectedReportingCompanies])

    useEffect(() => {
        setPage(0);
    }, [endDate, startDate, accountHolder, reportNameSearch])

    useEffect(() => {
        setOffset(page * rowsPerPage);
    }, [page])

    return (
        <div className={styles.container}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '50px' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <DeleteReportModal
                        open={showDeleteModal}
                        onClose={() => { setShowDeleteModal(false) }}
                        onDelete={handleDelete}
                        reportData={reportData}
                    />
                    <BrandTable
                        rows={items}
                        headCells={headCells}
                        checkboxless={true}
                        page={page}
                        hidePagination={true}
                        setSortBy={setSortBy}
                        setSortType={setSortType}
                        rowIsClickable={false}
                    />
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        component="div"
                        count={itemsCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => handleRowsPerPageChange(event, offset, setRowsPerPage, setPage, setOffset)}
                        ActionsComponent={TablePaginationActions}
                    />
                </>
            )}
        </div>
    )
}

export default CsvDataTable